import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { FaPlus } from "react-icons/fa";
import { BiSolidDownArrow } from "react-icons/bi";
import './DisplayCategories.css';

const DisplayCategories = () => {
  const navigate = useNavigate();

  // State management (100% Original Backend Logic)
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [rows, setRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [userLabId, setUserLabId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);

  // Load userLabId
  useEffect(() => {
    const labId = localStorage.getItem('userLabId');
    if (labId && labId.trim()) {
      setUserLabId(labId.trim());
    } else {
      setError('User Lab ID not found. Please login again.');
      setLoading(false);
    }
  }, []);

  // Fetch categories
  useEffect(() => {
    if (!userLabId) return;

    const fetchCategories = async () => {
      setLoading(true);
      setError(null);

      try {
        const res = await fetch(`https://hms.backend.hiragroupcloud.com/api/categories?userLabId=${userLabId}`);
        
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.message || 'Failed to fetch categories');
        }

        const data = await res.json();

        if (Array.isArray(data)) {
          const formatted = data.map((item, index) => ({
            id: item._id || item.id || `temp-${index}`,
            name: item.name || 'No Name',
            sortrank: item.sortrank || '',
            customId: item.customId || '',
          }));
          setRows(formatted);
        } else {
          setRows([]);
        }
      } catch (err) {
        setError('Failed to load categories. Please check server or try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, [userLabId]);

  // Toggle dropdown
  const toggleDropdown = (id) => {
    setOpenDropdownId(openDropdownId === id ? null : id);
  };

  // Open delete modal
  const openDeleteModal = (category) => {
    setCategoryToDelete(category);
    setShowDeleteModal(true);
    setOpenDropdownId(null);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setCategoryToDelete(null);
  };

  const confirmDelete = async () => {
    if (!categoryToDelete) return;

    try {
      const res = await fetch(`https://hms.backend.hiragroupcloud.com/api/categories/${categoryToDelete.id}`, {
        method: 'DELETE'
      });

      if (res.ok) {
        setRows(prev => prev.filter(row => row.id !== categoryToDelete.id));
        setSelectedRowId(null);
        closeDeleteModal();

        Swal.fire({
          title: 'Deleted!',
          text: 'Category deleted successfully!',
          icon: 'success',
          timer: 2000,
          timerProgressBar: true,
          showConfirmButton: false
        });
      } else {
        closeDeleteModal();
        Swal.fire({
          title: 'Error',
          text: 'Failed to delete category.',
          icon: 'error',
          confirmButtonColor: '#0052cc'
        });
      }
    } catch (err) {
      closeDeleteModal();
      Swal.fire({
        title: 'Error',
        text: 'Error deleting category. Please try again.',
        icon: 'error',
        confirmButtonColor: '#0052cc'
      });
    }
  };

  // Search filter
  const filteredRows = rows.filter(row =>
    Object.values(row).some(value =>
      value && value.toString().toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.dropdown-container')) {
        setOpenDropdownId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;
  const totalItems = filteredRows.length;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);
  const currentItems = filteredRows.slice(startIndex, endIndex);

  return (
    <div className="dc-container">
      {/* ===== PAGE HEADING ===== */}
      <h1 className="dc-heading">
        Display Categories
      </h1>

      {/* ===== MAIN CONTAINER ===== */}
      <div className="dc-main-wrapper">
        {/* ===== INNER WHITE CARD ===== */}
        <div className="dc-card">
          {/* ===== SEARCH + ADD ROW ===== */}
          <div className="dc-search-wrapper">
            {/* Search Section */}
            <div className="dc-search-box">
              <label className="dc-search-label">
                Search
              </label>
              <input
                type="text"
                placeholder="Type to search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                disabled={loading}
                className="dc-search-input"
              />
            </div>

            {/* Add Button */}
            <div className="dc-add-wrapper">
              <div className="dc-split-add-btn">
                <button
                  onClick={() => navigate('/add-category')}
                  disabled={loading}
                  className="dc-split-btn-left"
                >
                  <FaPlus className="dc-add-icon" />
                  Add
                </button>
                <button
                  onClick={() => navigate('/add-category')}
                  disabled={loading}
                  className="dc-split-btn-right"
                >
                  <BiSolidDownArrow className="dc-add-arrow" />
                </button>
              </div>
            </div>
          </div>

          {/* ===== ENTRIES TEXT ABOVE TABLE ===== */}
          <div className="dc-entries-text">
            Showing {totalItems > 0 ? `${startIndex + 1} to ${endIndex}` : '0'} of {totalItems} entries
          </div>

          {/* ===== LOADING / ERROR / NO DATA ===== */}
          {loading && (
            <div className="dc-msg">
              <div>Loading categories...</div>
            </div>
          )}

          {error && (
            <div className="dc-error">
              {error}
            </div>
          )}

          {!loading && !error && filteredRows.length === 0 && (
            <div className="dc-no-data">
              <h5 className="dc-no-data-title">No categories found</h5>
              <p>No categories added yet for your lab.</p>

              <div className="dc-split-add-btn" style={{ display: 'inline-flex', marginTop: '10px' }}>
                <button
                  onClick={() => navigate('/add-category')}
                  className="dc-split-btn-left"
                >
                  <FaPlus className="dc-add-icon" />
                  Add Your First Category
                </button>
                <button
                  onClick={() => navigate('/add-category')}
                  className="dc-split-btn-right"
                >
                  <BiSolidDownArrow className="dc-add-arrow" />
                </button>
              </div>
            </div>
          )}

          {/* ===== TABLE ===== */}
          {!loading && !error && filteredRows.length > 0 && (
            <div className="dc-table-wrap">
              <table className="dc-table">
                <thead>
                  <tr>
                    <th className="dc-th-name">
                      Name
                      <span className="dc-sort-icon">▲▼</span>
                    </th>
                    <th className="dc-th-sort">
                      Sort Rank
                      <span className="dc-sort-icon">▲▼</span>
                    </th>
                    <th className="dc-th-action">
                      {/* Actions */}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map((item) => (
                    <tr key={item.id} className="dc-table-row">
                      <td className="dc-td-name">
                        <div>{item.name}</div>
                      </td>

                      <td className="dc-td-sort">
                        {item.sortrank || ''}
                      </td>

                      <td className="dc-td-action">
                        <div className="dropdown-container">
                          <div className="dc-btn-group">
                            <button
                              className="dc-edit-btn"
                              onClick={() => navigate(`/edit-category/${item.id}`)}
                            >
                              Edit
                            </button>

                            <button
                              onClick={() => toggleDropdown(item.id)}
                              className="dc-dropdown-btn"
                            >
                              <BiSolidDownArrow />
                            </button>
                          </div>

                          {/* Dropdown Menu */}
                          {openDropdownId === item.id && (
                            <div className="dc-dropdown-menu">
                              <button
                                className="dc-dropdown-item"
                                onClick={() => {
                                  Swal.fire('Info', 'Assign Procedures coming soon!', 'info');
                                  setOpenDropdownId(null);
                                }}
                              >
                                Assign Procedures
                              </button>
                              <button
                                className="dc-dropdown-item"
                                onClick={() => {
                                  const category = rows.find(r => r.id === item.id);
                                  openDeleteModal(category);
                                }}
                              >
                                Delete
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* ===== PAGINATION FOOTER ===== */}
              <div className="dc-pagination-footer">
                <div className="dc-page-text">
                  Showing {totalItems > 0 ? `${startIndex + 1} to ${endIndex}` : '0'} of {totalItems} entries
                </div>
                <div className="dc-pagination-btns">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="dc-page-btn"
                  >
                    «
                  </button>
                  {Array.from({ length: Math.min(Math.ceil(totalItems / itemsPerPage), 5) }, (_, i) => {
                    const totalPages = Math.ceil(totalItems / itemsPerPage);
                    let pageNum;
                    if (totalPages <= 5) pageNum = i + 1;
                    else if (currentPage <= 3) pageNum = i + 1;
                    else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                    else pageNum = currentPage - 2 + i;
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`dc-page-btn ${currentPage === pageNum ? 'dc-active' : ''}`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(totalItems / itemsPerPage)))}
                    disabled={currentPage === Math.ceil(totalItems / itemsPerPage) || totalItems === 0}
                    className="dc-page-btn"
                  >
                    »
                  </button>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* ===== DELETE MODAL ===== */}
      {showDeleteModal && categoryToDelete && (
        <div className="dc-modal-overlay">
          <div className="dc-modal-content">
            <button onClick={closeDeleteModal} className="dc-modal-close">✕</button>

            <h2>Delete Category</h2>
            <div className="dc-modal-divider" />

            <p>Are you sure you want to delete</p>
            <p className="dc-modal-name">"{categoryToDelete.name}"?</p>
            <p className="dc-modal-warning">⚠️ This action cannot be undone.</p>

            <div className="dc-modal-actions">
              <button onClick={closeDeleteModal} className="dc-cancel-btn">Cancel</button>
              <button onClick={confirmDelete} className="dc-delete-btn">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DisplayCategories;